import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult
} from '@mediapipe/tasks-vision';

const WASM_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

/** Blendshape score above this counts as an eye closed. */
const BLINK_SCORE = 0.45;
/** Both eyes must stay closed at least this long (ms). */
const MIN_CLOSED_MS = 80;
/** Require eyes open again before accepting the blink. */
const REQUIRE_REOPEN = true;

let landmarkerPromise: Promise<FaceLandmarker> | null = null;

async function getFaceLandmarker(): Promise<FaceLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const fileset = await FilesetResolver.forVisionTasks(WASM_URL);
      return FaceLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: 'CPU'
        },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: true
      });
    })();
  }
  return landmarkerPromise;
}

function blendshapeScore(
  result: FaceLandmarkerResult,
  name: string
): number {
  const cats = result.faceBlendshapes?.[0]?.categories;
  if (!cats?.length) return 0;
  const hit = cats.find(c => c.categoryName === name);
  return hit?.score ?? 0;
}

function eyesClosed(result: FaceLandmarkerResult): boolean {
  const left = blendshapeScore(result, 'eyeBlinkLeft');
  const right = blendshapeScore(result, 'eyeBlinkRight');
  // Average both eyes so a wink is less likely to trigger.
  return (left + right) / 2 >= BLINK_SCORE;
}

export type BlinkWaitOptions = {
  timeoutMs?: number;
  onStatus?: (message: string) => void;
  signal?: AbortSignal;
};

/**
 * Watches the video stream until a blink is detected (close then reopen).
 * Returns when the blink completes so the caller can capture a frame.
 */
export async function waitForBlink(
  video: HTMLVideoElement,
  options?: BlinkWaitOptions
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 30000;
  const signal = options?.signal;
  const landmarker = await getFaceLandmarker();

  options?.onStatus?.('Look at the camera, then blink');

  return new Promise((resolve, reject) => {
    let closedAt: number | null = null;
    let sawClosed = false;
    let raf = 0;
    const started = performance.now();
    let lastTimestamp = -1;

    const cleanup = () => {
      if (raf) cancelAnimationFrame(raf);
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(new Error('Blink wait cancelled'));
    };

    if (signal) {
      if (signal.aborted) {
        reject(new Error('Blink wait cancelled'));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    const tick = () => {
      if (signal?.aborted) return;

      if (performance.now() - started > timeoutMs) {
        cleanup();
        reject(new Error('No blink detected. Try again and blink clearly.'));
        return;
      }

      if (video.readyState < 2) {
        raf = requestAnimationFrame(tick);
        return;
      }

      let timestamp = performance.now();
      if (timestamp <= lastTimestamp) timestamp = lastTimestamp + 1;
      lastTimestamp = timestamp;

      let result: FaceLandmarkerResult;
      try {
        result = landmarker.detectForVideo(video, timestamp);
      } catch {
        raf = requestAnimationFrame(tick);
        return;
      }

      if (!result.faceLandmarks?.length) {
        options?.onStatus?.('Position your face in the frame, then blink');
        closedAt = null;
        sawClosed = false;
        raf = requestAnimationFrame(tick);
        return;
      }

      const closed = eyesClosed(result);
      if (closed) {
        if (closedAt == null) closedAt = performance.now();
        if (performance.now() - closedAt >= MIN_CLOSED_MS) {
          sawClosed = true;
          options?.onStatus?.('Blink detected…');
        } else {
          options?.onStatus?.('Keep blinking…');
        }
      } else if (sawClosed) {
        if (REQUIRE_REOPEN) {
          cleanup();
          resolve();
          return;
        }
      } else {
        closedAt = null;
        options?.onStatus?.('Look at the camera, then blink');
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
  });
}

export function captureVideoFrame(video: HTMLVideoElement): Promise<Blob> {
  if (!video.videoWidth || !video.videoHeight) {
    return Promise.reject(new Error('Camera is not ready yet'));
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(new Error('Could not capture photo'));
  }

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) reject(new Error('Could not capture photo'));
        else resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
}
